import moment from 'moment';
import {
  colorButtonAJ,
  hitossVehicleRegistration,
  jobCancellationStatusA,
  jobCancellationStatusJ,
  load,
  MaintenanceTransaction,
  reporter,
  reporterBranch,
  roles,
  secretEncodeText,
  selfPropelled,
  sizeReturnOutMove,
  vehicleName,
  workingStatus,
  routeJustDoItCallApplication,
  regex,
} from './constants';
import { translate } from './intl';

/**
 * Parse value to new instance of Date
 *
 * @param {any} value
 * @returns {Date | null}
 */
export const parseDate = (value) => {
  if (!value) {
    return null;
  }

  const date = Date.parse(moment(value).format());

  if (date > 0) {
    return new Date(moment(value).format());
  }

  return null;
};

/**
 * Emulate react synthetic event class
 *
 * @param {Object}
 * @returns {Object}
 */
export const emulateSyntheticEvent = ({
  name,
  value = '',
  persist = () => {},
}) => ({ target: { name, value }, persist });

/**
 * Check value if it's not a function
 *
 * @param {*} value
 * @returns {Boolean}
 */
export const isNotAFunction = (value) => typeof value !== 'function';

/**
 * Flattens object in an object of API request responses
 *
 * @param {Object} obj
 * @returns {Object}
 */
export const objectifiedString = (obj) => {
  const output = {};

  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      if (!!obj[key].id) {
        output[key] = obj[key].id;
      }
    } else {
      output[key] = obj[key];
    }
  });

  return output;
};

/**
 * Format name display
 *
 * @param fileName
 * @returns {string|*}
 */
export const fileNameFormat = (fileName) => {
  if (fileName) {
    let cut = fileName.split(secretEncodeText).pop().split('.')[0];

    return fileName.replace(secretEncodeText + cut, '');
  }
  return '';
};

/**
 * Truncates strings to specified value
 *
 * @param {String} input
 * @param {Number} [chars=15]
 * @returns {String}
 */
export const truncate = (input, chars = 15) => {
  return input.length > chars ? `${input.substr(0, chars - 1)}\u2026` : input;
};

/**
 * Append date with current time
 *
 * @param date
 * @returns {moment.Moment}
 */
export const appendDateWithCurrentTime = (date) => {
  let formattedValue =
    moment(date).format('YYYY-MM-DD') + ' ' + moment().format('HH:mm');

  return moment(formattedValue);
};

export const validateMaxLengthInput = (event, handleChange, maxLength) => {
  const { value } = event.target;

  let countJP = 0;
  let countLatin = 0;
  for (let i = 0; i < value.length; i++) {
    if (
      value.charAt(i).match(/[\u30a0-\u30ff]/) ||
      value.charAt(i).match(/[\u3040-\u309f]/) ||
      value.charAt(i).match(/[\u4e00-\u9faf]/)
    ) {
      countJP++;
    } else {
      countLatin++;
    }
  }

  if (countJP * 2 + countLatin > maxLength) {
    return false;
  }

  return handleChange(event);
};

/**
 * Validate input is number
 *
 * @param event
 * @param length
 * @param handleChange
 */
export const validateNumber = (event, length, handleChange) => {
  const { value } = event.target;

  if (length && value.length > length) {
    return false;
  }
  //validate number
  const inputValue = value.replace(/[0-9]/g, '');
  if (inputValue === '') {
    handleChange(event);
  }
};

/**
 * Validate maxlength and break line
 *
 * @param event
 * @param length
 * @param handleChange
 * @param lengthBreak
 */
export const validateLengthInputBreak = (event, length, handleChange, lengthBreak) => {
  const { value } = event.target;
  if (length > 0 && value && value.length > length) {
    return false;
  }

  let newValue = value;
  if (value && value.length > lengthBreak && value.length < length) {
    newValue = value.replace(/[\n\r]+/g, '').replace(/(.{50})/g, '$1\n');
  }

  return handleChange({
    target: {
      name: event.target.name,
      value: newValue,
    },
  });
};

export const validateNumberWithComma = (event, length, handleChange) => {
  const { value } = event.target;

  if (length && value.length > length) {
    return false;
  }
  //validate number
  const inputValue = value.replaceAll(',', '').replace(/[0-9]/g, '');

  if (inputValue === '') {
    handleChange({
      target: {
        name: event.target.name,
        value: value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      },
    });
  }
};

export const validateNumberWithMinus = (event, length, handleChange) => {
  const { value } = event.target;

  if (length && value.length > length) {
    return false;
  }

  const inputValue = value.replace(/[0-9\-]/g, '');
  if (inputValue === '') {
    handleChange(event);
  }
};

/**
 * Validate length input
 *
 * @param event
 * @param length
 * @param handleChange
 * @returns {boolean|*}
 */
export const validateLengthInput = (event, length, handleChange) => {
  const { value } = event.target;
  if (length > 0 && value && value.length > length) {
    return false;
  }

  return handleChange(event);
};

export const validateLengthWithValue = (event, length, handleChange) => {
  const { value, name } = event.target;
  if (length > 0 && value.length > length) {
    return false;
  }

  return handleChange(name, value);
};

export const validateNumberWithValue = (event, length, handleChange) => {
  let value = event.target.value;

  if (length) {
    event.target.value = value.replace(/[^0-9]/g, '').substring(0, length);
    return handleChange(event);
  }
  event.target.value = value.replace(/[^0-9]/g, '');
  return handleChange(event);
};

/**
 * Validate input is text Latin
 *
 * @param event
 * @param length
 * @param handleChange
 * @returns {boolean|*}
 */
export const validateLatinInput = (event, length, handleChange) => {
  const regex = /[\u3000-\u303F]|[\u3040-\u309F]|[\u30A0-\u30FF]|[\uFF00-\uFFEF]|[\u4E00-\u9FAF]|[\u2605-\u2606]|[\u2190-\u2195]|\u203B|[`~!@#$%^&*()_+=<>,./?:;{}|'"\]\[\\\-]/g;
  const { value } = event.target;
  if ((length > 0 && value.length > length) || regex.test(value)) {
    return false;
  }

  return handleChange(event);
};

/**
 * Check if account is external
 *
 * @param type
 * @returns {boolean}
 */
export const isExternalAccount = (type) => {
  return type === roles.carDealer || type === roles.carMaker;
};

export const isCarDealerAccount = (listRoles) => {
  if (listRoles) {
    return listRoles.includes(roles.carDealer);
  }
  return false;
};

/**
 * Check if account is carDealer
 *
 * @param type
 * @returns {boolean}
 */
export const isCarDerlerlAccount = (type) => {
  return type === roles.carDealer;
};

/**
 * Check if account is carMaker
 *
 * @param type
 * @returns {boolean}
 */
export const isCarMakerAccount = (type) => {
  return type === roles.carMaker;
};

/**
 * Check if account is dealer
 *
 * @param type
 * @returns {boolean}
 */
export const isDealerAccount = (type) => {
  return type === roles.carDealer;
};

/**
 * Check if account is call center or administrator
 *
 * @param type
 * @returns {boolean}
 */
export const isCallCenterAccount = (type) => {
  return type === roles.callCenterAgent || type === roles.administrator;
};

/**
 * Get day of specific month from distance between that month and current month
 *
 * @param month
 * @returns {string}
 */
export const getDayOfMonth = (month) => {
  return moment().subtract(month, 'months').format('YYYY-MM-DD 00:00:00');
};

/**
 * Get today
 *
 * @returns {string}
 */
export const getToday = () => {
  return moment().format('YYYY-MM-DD 23:59:59');
};

/**
 * Validate input is text Latin EN
 *
 * @param event
 * @param length
 * @param handleChange
 * @returns {boolean|*}
 */
export const validateLatinInputEN = (event, length, handleChange) => {
  const regex = /[\xAA\xB5\xB7\xBA\xC0-\xD6\xD8-\xF6\xF8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0300-\u0374\u0376\u0377\u037A-\u037D\u0386-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u0483-\u0487\u048A-\u0523\u0531-\u0556\u0559\u0561-\u0587\u0591-\u05BD\u05BF\u05C1\u05C2\u05C4\u05C5\u05C7\u05D0-\u05EA\u05F0-\u05F2\u0610-\u061A\u0621-\u065E\u0660-\u0669\u066E-\u06D3\u06D5-\u06DC\u06DF-\u06E8\u06EA-\u06FC\u06FF\u0710-\u074A\u074D-\u07B1\u07C0-\u07F5\u07FA\u0901-\u0939\u093C-\u094D\u0950-\u0954\u0958-\u0963\u0966-\u096F\u0971\u0972\u097B-\u097F\u0981-\u0983\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BC-\u09C4\u09C7\u09C8\u09CB-\u09CE\u09D7\u09DC\u09DD\u09DF-\u09E3\u09E6-\u09F1\u0A01-\u0A03\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A3C\u0A3E-\u0A42\u0A47\u0A48\u0A4B-\u0A4D\u0A51\u0A59-\u0A5C\u0A5E\u0A66-\u0A75\u0A81-\u0A83\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABC-\u0AC5\u0AC7-\u0AC9\u0ACB-\u0ACD\u0AD0\u0AE0-\u0AE3\u0AE6-\u0AEF\u0B01-\u0B03\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3C-\u0B44\u0B47\u0B48\u0B4B-\u0B4D\u0B56\u0B57\u0B5C\u0B5D\u0B5F-\u0B63\u0B66-\u0B6F\u0B71\u0B82\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BBE-\u0BC2\u0BC6-\u0BC8\u0BCA-\u0BCD\u0BD0\u0BD7\u0BE6-\u0BEF\u0C01-\u0C03\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C33\u0C35-\u0C39\u0C3D-\u0C44\u0C46-\u0C48\u0C4A-\u0C4D\u0C55\u0C56\u0C58\u0C59\u0C60-\u0C63\u0C66-\u0C6F\u0C82\u0C83\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBC-\u0CC4\u0CC6-\u0CC8\u0CCA-\u0CCD\u0CD5\u0CD6\u0CDE\u0CE0-\u0CE3\u0CE6-\u0CEF\u0D02\u0D03\u0D05-\u0D0C\u0D0E-\u0D10\u0D12-\u0D28\u0D2A-\u0D39\u0D3D-\u0D44\u0D46-\u0D48\u0D4A-\u0D4D\u0D57\u0D60-\u0D63\u0D66-\u0D6F\u0D7A-\u0D7F\u0D82\u0D83\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0DCA\u0DCF-\u0DD4\u0DD6\u0DD8-\u0DDF\u0DF2\u0DF3\u0E01-\u0E3A\u0E40-\u0E4E\u0E50-\u0E59\u0E81\u0E82\u0E84\u0E87\u0E88\u0E8A\u0E8D\u0E94-\u0E97\u0E99-\u0E9F\u0EA1-\u0EA3\u0EA5\u0EA7\u0EAA\u0EAB\u0EAD-\u0EB9\u0EBB-\u0EBD\u0EC0-\u0EC4\u0EC6\u0EC8-\u0ECD\u0ED0-\u0ED9\u0EDC\u0EDD\u0F00\u0F18\u0F19\u0F20-\u0F29\u0F35\u0F37\u0F39\u0F3E-\u0F47\u0F49-\u0F6C\u0F71-\u0F84\u0F86-\u0F8B\u0F90-\u0F97\u0F99-\u0FBC\u0FC6\u1000-\u1049\u1050-\u1099\u10A0-\u10C5\u10D0-\u10FA\u10FC\u1100-\u1159\u115F-\u11A2\u11A8-\u11F9\u1200-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u135F\u1369-\u1371\u1380-\u138F\u13A0-\u13F4\u1401-\u166C\u166F-\u1676\u1681-\u169A\u16A0-\u16EA\u16EE-\u16F0\u1700-\u170C\u170E-\u1714\u1720-\u1734\u1740-\u1753\u1760-\u176C\u176E-\u1770\u1772\u1773\u1780-\u17B3\u17B6-\u17D3\u17D7\u17DC\u17DD\u17E0-\u17E9\u180B-\u180D\u1810-\u1819\u1820-\u1877\u1880-\u18AA\u1900-\u191C\u1920-\u192B\u1930-\u193B\u1946-\u196D\u1970-\u1974\u1980-\u19A9\u19B0-\u19C9\u19D0-\u19D9\u1A00-\u1A1B\u1B00-\u1B4B\u1B50-\u1B59\u1B6B-\u1B73\u1B80-\u1BAA\u1BAE-\u1BB9\u1C00-\u1C37\u1C40-\u1C49\u1C4D-\u1C7D\u1D00-\u1DE6\u1DFE-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u200C\u200D\u203F\u2040\u2054\u2071\u207F\u2090-\u2094\u20D0-\u20DC\u20E1\u20E5-\u20F0\u2102\u2107\u210A-\u2113\u2115\u2118-\u211D\u2124\u2126\u2128\u212A-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2160-\u2188\u2C00-\u2C2E\u2C30-\u2C5E\u2C60-\u2C6F\u2C71-\u2C7D\u2C80-\u2CE4\u2D00-\u2D25\u2D30-\u2D65\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2DE0-\u2DFF\u3005-\u3007\u3021-\u302F\u3031-\u3035\u3038-\u303C\u3041-\u3096\u3099-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312D\u3131-\u318E\u31A0-\u31B7\u31F0-\u31FF\u3400-\u4DB5\u4E00-\u9FC3\uA000-\uA48C\uA500-\uA60C\uA610-\uA62B\uA640-\uA65F\uA662-\uA66F\uA67C\uA67D\uA67F-\uA697\uA717-\uA71F\uA722-\uA788\uA78B\uA78C\uA7FB-\uA827\uA840-\uA873\uA880-\uA8C4\uA8D0-\uA8D9\uA900-\uA92D\uA930-\uA953\uAA00-\uAA36\uAA40-\uAA4D\uAA50-\uAA59\uAC00-\uD7A3\uF900-\uFA2D\uFA30-\uFA6A\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE00-\uFE0F\uFE20-\uFE26\uFE33\uFE34\uFE4D-\uFE4F\uFE70-\uFE74\uFE76-\uFEFC\uFF10-\uFF19\uFF21-\uFF3A\uFF3F\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC]|\uD800[\uDC00-\uDC0B\uDC0D-\uDC26\uDC28-\uDC3A\uDC3C\uDC3D\uDC3F-\uDC4D\uDC50-\uDC5D\uDC80-\uDCFA\uDD40-\uDD74\uDDFD\uDE80-\uDE9C\uDEA0-\uDED0\uDF00-\uDF1E\uDF30-\uDF4A\uDF80-\uDF9D\uDFA0-\uDFC3\uDFC8-\uDFCF\uDFD1-\uDFD5]|\uD801[\uDC00-\uDC9D\uDCA0-\uDCA9]|\uD802[\uDC00-\uDC05\uDC08\uDC0A-\uDC35\uDC37\uDC38\uDC3C\uDC3F\uDD00-\uDD15\uDD20-\uDD39\uDE00-\uDE03\uDE05\uDE06\uDE0C-\uDE13\uDE15-\uDE17\uDE19-\uDE33\uDE38-\uDE3A\uDE3F]|\uD808[\uDC00-\uDF6E]|\uD809[\uDC00-\uDC62]|\uD834[\uDD65-\uDD69\uDD6D-\uDD72\uDD7B-\uDD82\uDD85-\uDD8B\uDDAA-\uDDAD\uDE42-\uDE44]|\uD835[\uDC00-\uDC54\uDC56-\uDC9C\uDC9E\uDC9F\uDCA2\uDCA5\uDCA6\uDCA9-\uDCAC\uDCAE-\uDCB9\uDCBB\uDCBD-\uDCC3\uDCC5-\uDD05\uDD07-\uDD0A\uDD0D-\uDD14\uDD16-\uDD1C\uDD1E-\uDD39\uDD3B-\uDD3E\uDD40-\uDD44\uDD46\uDD4A-\uDD50\uDD52-\uDEA5\uDEA8-\uDEC0\uDEC2-\uDEDA\uDEDC-\uDEFA\uDEFC-\uDF14\uDF16-\uDF34\uDF36-\uDF4E\uDF50-\uDF6E\uDF70-\uDF88\uDF8A-\uDFA8\uDFAA-\uDFC2\uDFC4-\uDFCB\uDFCE-\uDFFF]|[\uD840-\uD868][\uDC00-\uDFFF]|\uD869[\uDC00-\uDED6]|\uD87E[\uDC00-\uDE1D]|\uDB40[\uDD00-\uDDEF][\u3000-\u303F]|[\u3040-\u309F]|[\u30A0-\u30FF]|[\uFF00-\uFFEF]|[\u4E00-\u9FAF]|[\u2605-\u2606]|[\u2190-\u2195]|\u203B|[`~!@#$%^&*()_+=<>,./?:;{}|'"\]\[\\\-]/g;
  const { value } = event.target;
  if ((length > 0 && value.length > length) || regex.test(value)) {
    return false;
  }

  return handleChange(event);
};

/**
 * Validate input is any regex style
 *
 * @param event
 * @param regex
 * @param length
 * @param handleChange
 * @returns {boolean|*}
 */
export const validateAnyStyleInput = (event, regex, length, handleChange) => {
  const { value } = event.target;
  if (length > 0 && value.length > length) {
    return false;
  }
  if (regex.test(value) || value === '') {
    return handleChange(event);
  }
  return false;
};

export const getUploadedFileUrl = (inquiryId, name) => {
  const baseURL = process.env.REACT_APP_API_URL.replace(/\/$/, '');
  return encodeURI(
    `${baseURL}/uploaded/hinoConnect/inquiryForm/${inquiryId}` + `/${name}`,
  );
};

export const getTypeFromFileName = (name = '') => {
  if (!name) return '';

  if (name.match(/\.(pdf|PDF)$/)) return 'pdf';

  if (name.match(/\.(xls|XLS|xlsx|XLSX)$/)) return 'excel';

  if (name.match(/\.(jpg|jpeg|png|bmp|gif)$/i)) return 'image';

  return '';
};

export const getImportedFileUrl = (name) => {
  const baseURL = process.env.REACT_APP_API_URL.replace(/\/$/, '');
  return encodeURI(`${baseURL}/uploaded/hinoConnect/importFile/${name}`).replace('&', '%26');
};

export const getParamsFromQueryString = (queryStr, params) => {
  const regex = new RegExp(`(^.*)([?&]${params}=)([^&#]*)(.*$)`);
  return queryStr.replace(regex, '$3');
};

export const checkWorkReport = (workReports) => {
  let workReport = [];
  if (workReports && workReports.length > 0) {
    workReport = workReports.map((workReports) => {
      if (
        workReports.complete_time ||
        (workReports.treatment && workReports.treatment_result)
      ) {
        return 1;
      } else return 0;
    });
  }
  return workReport;
};

export const formatDataTable = (data) => {
  let dataHinoSos = [];
  if (data && data.length > 0) {
    dataHinoSos = data.map((value) => {
      return {
        ...value,
        completion_work_name: changeTextCompletionWorkName(value),
        communication: changeTextCommunication(value),
      };
    });
  }

  return dataHinoSos;
};

export const formatDataTableConnect = (data) => {
  let dataHinoConnect = [];
  if (data && data.length > 0) {
    dataHinoConnect = data.map((value) => {
      return {
        ...value,
        inquiry_status: changeTextInquiryStatus(value),
      };
    });
  }

  return dataHinoConnect;
};

export const formatDataListDM = (data) => {
  let dataDMShipping = [];
  if (data && data.length > 0) {
    dataDMShipping = data.map((value) => {
      return {
        ...value,
      };
    });
  }

  return dataDMShipping;
};

export const formatDataTableJustDoIt = (data) => {
  let dataJustDoIt = [];
  if (data && data.length > 0) {
    dataJustDoIt = data.map((value) => {
      return {
        ...value,
        communication: changeTextCommunication(value),
      };
    });
  }

  return dataJustDoIt;
};

export const changeTextCompletionWorkName = (value) => {
  const module = { page: 'Hino SOS', tab: 'Inquiry List' };
  const reportedStatus =
    translate('Done Status') + '/' + translate('Reported Status');
  let completionWorkName = '';
  if (value.caller_completion_work == '有' || value.car_dealer_completion_work == '有') {
    let workReport = [];
    workReport = checkWorkReport(value.work_reports);
    completionWorkName =
      workReport.indexOf(1) > -1
        ? translate('Entered', module)
        : translate('Awaiting report', module);
  } else if (
    value.caller_completion_work === reportedStatus &&
    value.caller_completion_arrangement === reportedStatus &&
    value.car_dealer_completion_work === reportedStatus
  ) {
    completionWorkName = translate('Reported', module);
  } else {
    completionWorkName = null;
  }

  return completionWorkName;
};

export const changeTextCommunication = (value) => {
  let receiver_name_kana, communication;
  receiver_name_kana = value.receiver_name_kana ? value.receiver_name_kana : '';
  communication = value.communication ? value.communication : '';

  return [receiver_name_kana, communication];
};

export const changeTextInquiryStatus = (value) => {
  let receiver_name_kana, inquiry_status;
  receiver_name_kana = value.receiver_name_kana ? value.receiver_name_kana : '';
  inquiry_status = value.inquiry_status ? value.inquiry_status : '';

  return [inquiry_status, receiver_name_kana];
};

export const checkFileType = (fileType) => {
  switch (fileType) {
    case 'application/pdf':
      return (fileType = 'pdf');
      break;

    case 'image/png':
    case 'image/jpg':
    case 'image/gif':
    case 'image/jpeg':
    case 'image/bmp':
      return (fileType = 'image');
      break;

    case 'application/vnd.ms-excel':
    case 'application/msexcel':
    case 'application/x-msexcel':
    case 'application/x-ms-excel':
    case 'application/x-excel':
    case 'application/x-dos_ms_excel':
    case 'application/xls':
    case 'application/x-xls':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return (fileType = 'excel');
      break;

    default:
      return (fileType = '');
  }
};

/**
 * Check cases to render color button A
 *
 * @param aData
 * @returns {string}
 */
export const renderColorButtonA = (aData) => {
  const isWorking = aData.status;
  const status = aData.job_cancellation_status;
  const reason = aData.job_cancellation_reason;
  switch (status) {
    case jobCancellationStatusA.beforeDispatch:
      return colorButtonAJ.brown;
    case jobCancellationStatusA.afterDispatch:
      return colorButtonAJ.cyan;
    default:
      break;
  }
  if (isWorking === workingStatus.inactive && !status && !reason) {
    return colorButtonAJ.green;
  }
  if (
    isWorking === workingStatus.active &&
    !status &&
    !reason &&
    (aData.outsourced_staff_number ||
      aData.outsourced_staff_name ||
      aData.outsourced_staff_info)
  ) {
    return colorButtonAJ.blue;
  }
  if (
    isWorking === workingStatus.active &&
    !status &&
    !reason &&
    !aData.outsourced_staff_number &&
    !aData.outsourced_staff_name &&
    !aData.outsourced_staff_info
  ) {
    return colorButtonAJ.yellow;
  }
  return '';
};

/**
 * Check cases to render color button J
 *
 * @param jData
 * @returns {string}
 */
export const renderColorButtonJ = (jData) => {
  const isWorking = jData.status;
  const status = jData.job_cancellation_status;
  const reason = jData.job_cancellation_reason;
  switch (status) {
    case jobCancellationStatusJ.beforeDispatch:
      return colorButtonAJ.brown;
    case jobCancellationStatusJ.beforeDispatchArrival:
    case jobCancellationStatusJ.afterDispatchArrival:
      return colorButtonAJ.cyan;
    default:
      break;
  }
  if (isWorking === workingStatus.active && !status && !reason) {
    return colorButtonAJ.yellow;
  }
  if (isWorking === workingStatus.inactive && !status && !reason) {
    return colorButtonAJ.green;
  }
  return '';
};

export const addOrRemoveButtonId = (containButton, array, id) => {
  if (containButton && array.indexOf(id) === -1) {
    array.push(id);
  } else if (!containButton && array.indexOf(id) !== -1) {
    const index = array.indexOf(id);
    array.splice(index, 1);
  }
  return array;
};

/**
 * Remove last character of given string
 *
 * @param string
 * @returns {*}
 */
export const removeLastCharacterOfString = (string) => {
  return string.slice(0, -1);
};

/**
 * Scroll to div
 *
 * @param ref
 * @returns {*}
 */
export const scrollToRef = (ref) => {
  if (ref.current.offsetTop < 500) {
    window.scrollTo(0, ref.current.offsetTop - 130);
  } else {
    window.scrollTo(0, ref.current.offsetTop);
  }
};

export const getUploadedUrl = (path) => {
  const baseURL = process.env.REACT_APP_API_URL.replace(/\/$/, '');
  return encodeURI(`${baseURL}/uploaded/${path}`);
};

export const getSelfPropelled = (value) => {
  switch (parseInt(value)) {
    case 0:
      return selfPropelled[0];
    case 1:
      return selfPropelled[1];
    default:
      return value;
  }
};

export const getLoad = (value) => {
  switch (parseInt(value)) {
    case 0:
      return load[0];
    case 1:
      return load[1];
    default:
      return value;
  }
};

export const getMaintenanceTransaction = (value) => {
  switch (parseInt(value)) {
    case 0:
      return MaintenanceTransaction[0];
    case 1:
      return MaintenanceTransaction[1];
    default:
      return value;
  }
};
export const getHitossVehicleRegistration = (value) => {
  switch (parseInt(value)) {
    case 0:
      return hitossVehicleRegistration[0];
    case 1:
      return hitossVehicleRegistration[1];
    case 2:
      return hitossVehicleRegistration[2];
    default:
      return value;
  }
};

export const getVehicleName = (value) => {
  switch (parseInt(value)) {
    case 1:
      return vehicleName[1];
    case 2:
      return vehicleName[2];
    case 3:
      return vehicleName[3];
    case 4:
      return vehicleName[4];
    case 5:
      return vehicleName[5];
    case 6:
      return vehicleName[6];
    case 7:
      return vehicleName[7];
    case 8:
      return vehicleName[8];
    case 9:
      return vehicleName[9];
    case 10:
      return vehicleName[10];
    case 11:
      return vehicleName[11];
    case 12:
      return vehicleName[12];
    case 13:
      return vehicleName[13];
    case 14:
      return vehicleName[14];
    default:
      return value;
  }
};

export const getReporter = (value) => {
  switch (parseInt(value)) {
    case 1:
      return reporter[1];
    case 2:
      return reporter[2];
    case 3:
      return reporter[3];
    case 4:
      return reporter[4];
    case 5:
      return reporter[5];
    case 6:
      return reporter[6];
    case 7:
      return reporter[7];
    case 8:
      return reporter[8];
    case 9:
      return reporter[9];
    case 10:
      return reporter[10];
    case 11:
      return reporter[11];
    case 12:
      return reporter[12];
    case 13:
      return reporter[13];
    case 14:
      return reporter[14];
    case 15:
      return reporter[15];
    case 16:
      return reporter[16];
    case 17:
      return reporter[17];
    case 18:
      return reporter[18];
    case 19:
      return reporter[19];
    case 20:
      return reporter[20];
    case 21:
      return reporter[21];
    case 22:
      return reporter[22];
    case 23:
      return reporter[23];
    case 24:
      return reporter[24];
    case 25:
      return reporter[25];
    case 26:
      return reporter[26];
    case 27:
      return reporter[27];
    case 28:
      return reporter[28];
    case 29:
      return reporter[29];
    case 30:
      return reporter[30];
    case 31:
      return reporter[31];
    case 32:
      return reporter[32];
    case 33:
      return reporter[33];
    case 34:
      return reporter[34];
    case 35:
      return reporter[35];
    case 36:
      return reporter[36];
    case 37:
      return reporter[37];
    case 38:
      return reporter[38];
    case 39:
      return reporter[39];
    case 40:
      return reporter[40];
    case 41:
      return reporter[41];
    case 42:
      return reporter[42];
    case 43:
      return reporter[43];
    default:
      return '';
  }
};

export const getReporterBranch = (value) => {
  switch (parseInt(value)) {
    case 7:
      return reporterBranch[7];
    case 8:
      return reporterBranch[8];
    case 9:
      return reporterBranch[9];
    case 10:
      return reporterBranch[10];
    case 11:
      return reporterBranch[11];
    case 12:
      return reporterBranch[12];
    case 13:
      return reporterBranch[13];
    case 14:
      return reporterBranch[14];
    case 15:
      return reporterBranch[15];
    default:
      return '';
  }
};

export const convertDateTimeFormat = (datetime, index, controlData) => {
  if (datetime && datetime != '/') {
    const date = moment(datetime).toDate();
    if (index === 1) {
      return `${date.getFullYear()}年${convertTimeStamp(
        date.getMonth() + 1,
      )}月${convertTimeStamp(date.getDate())}日 ${convertTimeStamp(
        date.getHours(),
      )}:${convertTimeStamp(date.getMinutes())}`;
    } else if (index === 3) {
      return `${date.getFullYear()}/${convertTimeStamp(
        date.getMonth() + 1,
      )}/${convertTimeStamp(date.getDate())} ${convertTimeStamp(
        date.getHours(),
      )}:${convertTimeStamp(date.getMinutes())}`;
    } else if (index === 4) {
      const hours = convertTimeStamp(date.getHours());
      const minutes = convertTimeStamp(date.getMinutes());
      return `${hours}:${minutes}`;
    } else {
      if (controlData && controlData.era_name) {
        const monthYear = datetime.split('/');
        const era = controlData.era_name;
        const lenEra = era.length;
        let year = '';
        for (let i = 0; i < lenEra; i++) {
          if (era[i].year === parseInt(monthYear[0])) {
            year = era[i].name;
            break;
          }
        }
        let result = year.replace('/', '(');
        result += ')/';
        return `${result}${('0' + monthYear[1]).slice(-2)}月`;
      }
    }
  } else {
    return null;
  }
};

export const getReporterName = (value) => {
  if (value) {
    return value;
  } else {
    return '';
  }
};

export const chunkArray = (myArray, chunk_size) => {
  var index = 0;
  var arrayLength = myArray ? myArray.length : 0;
  var tempArray = [];

  for (index = 0; index < arrayLength; index += chunk_size) {
    var myChunk = myArray.slice(index, index + chunk_size);
    tempArray.push(myChunk);
  }

  return tempArray;
};

export const setFilename = (idSelected, data) => {
  let filename = '';
  if (idSelected.length > 1) {
    let date = new Date();
    let month = ('0' + (date.getMonth() + 1)).slice(-2);
    let day = ('0' + date.getDate()).slice(-2);

    filename = 'SOS' + date.getFullYear() + month + day;
  } else {
    var reception_number = data[0].reception_number
      ? data[0].reception_number.replace(/\s/g, '')
      : null;
    var vehicle_registration_number_0 = data[0].vehicle_registration_number
      .replace(/ /gi, '-')
      .split('-')[0];
    var vehicle_registration_number_1 = data[0].vehicle_registration_number
      .replace(/ /gi, '-')
      .split('-')[1];
    var vehicle_registration_number_2 = data[0].vehicle_registration_number
      .replace(/ /gi, '-')
      .split('-')[2];
    var vehicle_registration_number_3 = data[0].vehicle_registration_number
      .replace(/ /gi, '-')
      .split('-')[3];
    var vehicle_registration_number =
      data[0].vehicle_registration_number ||
      vehicle_registration_number_0 ||
      vehicle_registration_number_1 ||
      vehicle_registration_number_2 ||
      vehicle_registration_number_3
        ? '_' +
          vehicle_registration_number_0 +
          vehicle_registration_number_1 +
          vehicle_registration_number_2 +
          vehicle_registration_number_3
        : '';
    var billing_name = data[0].billing_name
      ? '_' + data[0].billing_name.replace(/\s/g, '')
      : '';

    filename = `${reception_number}${vehicle_registration_number}${billing_name}`;
  }

  return filename;
};

export const getCarNumber = (number) => {
  if (number && number !== '---') {
    let result = number.replace(/-/g, ' ');
    result = result.trim();
    return `${result}`;
  }
  return '';
};

export const convertTimeStamp = (time) => {
  if (time) {
    return ('0' + time).slice(-2);
  }
  return '';
};

export const getOperatingSystem = (window) => {
  let operatingSystem = 'Not known';
  if (window.navigator.appVersion.indexOf('Win') !== -1) {
    operatingSystem = 'Windows OS';
  }
  if (window.navigator.appVersion.indexOf('Mac') !== -1) {
    operatingSystem = 'MacOS';
  }
  if (window.navigator.appVersion.indexOf('X11') !== -1) {
    operatingSystem = 'UNIX OS';
  }
  if (window.navigator.appVersion.indexOf('Linux') !== -1) {
    operatingSystem = 'Linux OS';
  }

  return operatingSystem;
};

export const getBrowser = (window) => {
  let currentBrowser = 'Not known';
  if (window.navigator.userAgent.indexOf('Chrome') !== -1) {
    currentBrowser = 'Google Chrome';
  } else if (window.navigator.userAgent.indexOf('Firefox') !== -1) {
    currentBrowser = 'Mozilla Firefox';
  } else if (window.navigator.userAgent.indexOf('MSIE') !== -1) {
    currentBrowser = 'Internet Exployer';
  } else if (window.navigator.userAgent.indexOf('Edge') !== -1) {
    currentBrowser = 'Edge';
  } else if (window.navigator.userAgent.indexOf('Safari') !== -1) {
    currentBrowser = 'Safari';
  } else if (window.navigator.userAgent.indexOf('Opera') !== -1) {
    currentBrowser = 'Opera';
  } else if (window.navigator.userAgent.indexOf('Opera') !== -1) {
    currentBrowser = 'YaBrowser';
  } else {
    console.log('Others');
  }

  return currentBrowser;
};

export const isObjectEmpty = (object) => {
  return Object.keys(object).length === 0;
};

export const setValueToObjectKey = (object, result) => {
  for (let [key, value] of Object.entries(object)) {
    if (value && typeof value !== 'string' && value !== '') {
      result[key] = value;
    } else if (
      value &&
      typeof value === 'string' &&
      value.split(' ').join('') !== ''
    ) {
      result[key] = value;
    }
  }
  return result;
};

/**
 * Convert fullWidth to halfwidth
 * @param fullWidthNum
 * @returns {number|*}
 */

export const fullWidthNumConvert = (fullWidthNum) => {
  let halfsizeNumber = '';
  return fullWidthNum.replace(/[\uFF10-\uFF19]/g, function (m) {
    halfsizeNumber = String.fromCharCode(m.charCodeAt(0) - 0xfee0);
    return halfsizeNumber;
  });
};

export const convertUsercode = (userCode) => {
  if (userCode.includes('-')) {
    const index = userCode.indexOf('-');
    return userCode.slice(0, index);
  } else {
    return userCode;
  }
};

export const isValidAudioFile = (filename) => {
  return !!filename.match(
    /(\.mp3|\.ogg|\.MP3|\.OGG|\.m4a|\.m4b|\.m4p|\.m4v|\.m4r|\.aac|\.oga|\.wma|\.wav)$/,
  );
};

export const dontActiveRoot = () => {
  document.getElementById('root').style.pointerEvents = 'none';
  document.getElementById('root').style.userSelect = 'none';
  document.getElementById('root').style.webkitUserSelect = 'none';
  document.getElementById('root').style.msUserSelect = 'none';
  document.getElementById('root').style['-moz-user-select'] = 'none';
};

export const activeRoot = () => {
  document.getElementById('root').style.pointerEvents = 'auto';
  document.getElementById('root').style.userSelect = 'auto';
  document.getElementById('root').style.msUserSelect = 'auto';
  document.getElementById('root').style.webkitUserSelect = 'auto';
  document.getElementById('root').style['-moz-user-select'] = 'auto';
};

export const onStopDraggable = (event, data) => {
  const bounding = data.node.getBoundingClientRect();

  const documentElement = document.documentElement;
  const wrapperHeight = documentElement.clientHeight || window.innerHeight;
  const wrapperWidth = documentElement.clientWidth || window.innerWidth;
  const elementWidth = bounding.width,
    elementHeight = bounding.height;
  let moveX = data.x,
    moveY = data.y;

  if (moveY < 0 && moveY < -elementHeight + sizeReturnOutMove) {
    moveY = -elementHeight + sizeReturnOutMove;
  }

  if (moveX < 0 && moveX < -elementWidth + sizeReturnOutMove) {
    moveX = -elementWidth + sizeReturnOutMove;
  }

  if (moveY > wrapperHeight - sizeReturnOutMove) {
    moveY = wrapperHeight - sizeReturnOutMove;
  }

  if (moveX > wrapperWidth - sizeReturnOutMove) {
    moveX = wrapperWidth - sizeReturnOutMove;
  }

  return { x: moveX, y: moveY };
};

export const defaultPosition = () => {
  const documentElement = document.documentElement;
  const wrapperHeight = documentElement.clientHeight || window.innerHeight;
  const wrapperWidth = documentElement.clientWidth || window.innerWidth;

  return { x: wrapperWidth / 5, y: wrapperHeight / 3 };
};

export const formatDateTime = (dateTime) => {
  return moment(dateTime).format('YYYY/MM/DD HH:mm:ss');
};

export const formatDateTimeHours = (dateTime) => {
  return moment(dateTime).format('YYYY/MM/DD HH:mm');
};

export const formatTimeHours = (dateTime) => {
  return moment(dateTime).format('HH:mm');
};

export const formatDate = (date = new Date()) => {
  return moment(date).format('YYYY-MM-DD');
};

export const checkObjectHasData = (formState) => {
  let obj = { ...formState, id: '', current_tab: '' };
  return !Object.values(obj).every(
    (o) =>
      o == null || o == '' || o == 'undefined' || o == 'undefined:undefined:00',
  );
};

export const formatDateTimeJapan = () => {
  return moment().format('YYYY' + '年' + 'MM' + '月' + 'DD' + '日' + ' HH:mm ');
};

/**
 * Copy text to clipboad
 * @param string
 * @returns {boolean}
 */
export const copyTextToClipboard = (textVal) => {
  var copyFrom = document.createElement('textarea');
  copyFrom.textContent = textVal;

  var bodyElm = document.getElementsByTagName('body')[0];
  bodyElm.appendChild(copyFrom);

  copyFrom.select();
  var retVal = document.execCommand('copy');
  bodyElm.removeChild(copyFrom);
  return retVal;
};

export const checkJustDoItCallApplicationRoute = () => {
  return window.location.pathname === routeJustDoItCallApplication;
};

export const checkIsSet = (attribute) => {
  return (
    attribute !== null &&
    attribute !== undefined &&
    attribute.toString().trim() !== 'null' &&
    attribute.toString().trim() !== 'undefined' &&
    attribute.toString().trim() !== ''
  );
};

export const checkDataExist = (attribute) => {
  return (
    attribute !== null &&
    attribute !== undefined &&
    attribute !== 'null' &&
    attribute !== false &&
    attribute !== 'undefined' &&
    attribute.toString().trim() !== ''
  );
};
/**
 *
 * @param chartLabels
 * @param data
 * @returns {Array}
 */
export const createChartData = (chartLabels, data) => {
  const chartDataForm = {
    label: '',
    data: '',
  };
  let chartData = [];
  data.map((value) => {
    let rowData = [];
    Object.entries(value).forEach(([, val], index) => {
      if (chartLabels[index] !== undefined) {
        let dataObj = Object.create(chartDataForm);
        dataObj.label = chartLabels[index];
        dataObj.data = val;
        rowData.push(dataObj);
      } else {
        if (val.length % 2 === 0 && val.length === 4) {
          let labelString = [...val];
          let label = '';
          labelString.map((char, index) => {
            if (index > 0 && (index + 1) % 2 === 0) {
              label += labelString[index - 1] + labelString[index] + '\n';
            }
          });
          rowData[index] = label;
        } else {
          rowData[index] = val;
        }
      }
    });
    chartData.push(rowData);
  });
  return chartData;
};

export const splitChartLabel = (val) => {
  let chartLabel = '';
  if (val.length > 5 && val.length <= 10) {
    chartLabel = val.slice(0, 5) + '\n' + val.slice(5, val.length);
  } else if (val.length > 10) {
    chartLabel = val.slice(0, 5) + '\n' + val.slice(5, 10) + '\n' + val.slice(10, val.length);
  } else {
    chartLabel = val;
  }
  return chartLabel;
};

export function parseBoolean(data) {
  return checkIsSet(data) && data.toString() === 'true';
}

export const getOptionByCategoryCode = (masterCategories, categoryCode) => {
  if (masterCategories) {
    return masterCategories[categoryCode] ? masterCategories[categoryCode] : [];
  }

  return [];
};

export const convertNullToBlank = (attribute) => {
  return attribute !== null &&
    attribute !== undefined &&
    attribute.toString().trim() !== 'null' &&
    attribute.toString().trim() !== 'undefined' &&
    attribute.toString().trim() !== ''
    ? attribute
    : '';
};

export const handleNextHinoConnectFormDetail = () => {
  let currPage = window.location.pathname.split('/');
  let inquiryId = currPage.length === 4 ? parseInt(currPage[3]) : null;
  let datas = JSON.parse(localStorage.getItem('hinoConnectDataSearch'));
  datas.map((item, index) => {
    if (inquiryId === item) {
      if (index + 1 < datas.length) {
        window.location.href = '/hino-connect/inquiry/' + datas[index + 1];
      }
    }
  });
};

export const handleBackHinoConnectFormDetail = () => {
  let currPage = window.location.pathname.split('/');
  let inquiryId = currPage.length === 4 ? parseInt(currPage[3]) : null;
  let datas = JSON.parse(localStorage.getItem('hinoConnectDataSearch'));
  datas.map((item, index) => {
    if (inquiryId === item) {
      if (index - 1 >= 0) {
        window.location.href = '/hino-connect/inquiry/' + datas[index - 1];
      }
    }
  });
};

export const onBlurValidateMaxLengthInput = (
  event,
  handleChange,
  maxLength,
) => {
  const { value } = event.target;

  let countJP = 0;
  let countLatin = 0;
  for (let i = 0; i < value.length; i++) {
    if (
      value
        .charAt(i)
        .match(
          /[\u30a0-\u30ffｦ-ﾟ一-龠ぁ-ゔヶァ-ヴ０-９々〆〤ァ-ヴ゛゜　ーぁ-んァ-ン、あ-ん]/,
        ) ||
      value.charAt(i).match(/[\u3040-\u309f]/) ||
      value.charAt(i).match(/[\u4e00-\u9faf]/)
    ) {
      countJP++;
    } else {
      countLatin++;
    }
    if (countJP * 2 + countLatin > maxLength) {
      countJP--;
      break;
    }

    if (countJP * 2 + countLatin == maxLength) {
      break;
    }
  }
  event.target.value = value.slice(0, countJP + countLatin);
  return handleChange(event);
};

export const validateAutoHyphenCdCompanyCustomer = (
  event,
  length,
  handleChange,
) => {
  let value = event.target.value + '';
  value = value.replace(/[^0-9]/g, '');
  if (length > 0 && value.length > length) {
    return false;
  }
  event.target.value = value.replace(/(\d{3})(\d+)/, '$1-$2');
  return handleChange(event);
};

export const isMakerAccount = (type) => {
  return type === roles.carMaker;
};

export const onBlurFullSize = (event, length, handleChange) => {
  let value = event.target.value;
  value = value.replace(
    regex.japanese_replace,
    '',
  );
  value = value.substring(0, length);
  event.target.value = value;
  handleChange(event);
}
